import type { ServiceRecord } from "./types";

/**
 * The six services, one per page under `src/app/solutions/`.
 *
 * The German summaries are the descriptions those pages publish in their own
 * metadata, so an answer and the page a visitor lands on say the same thing.
 */
export const SERVICES: readonly ServiceRecord[] = [
  {
    id: "ai-automation",
    route: "solutions-ai-automation",
    name: { de: "KI & Automation", en: "AI & Automation" },
    summary: {
      de: "RAG-Systeme, KI-Agenten, MCP-Server und n8n-Automationen für österreichische KMUs und Enterprise-Kunden.",
      en: "RAG systems, AI agents, MCP servers and n8n automations for Austrian SMEs and enterprise clients.",
    },
    covers: {
      de: [
        "RAG-Systeme auf eigenen Dokumenten",
        "KI-Agenten und MCP-Server",
        "Prozessautomatisierung mit n8n",
        "DSGVO-konformer Betrieb, auf Wunsch on-premise mit Ollama",
      ],
      en: [
        "RAG systems over your own documents",
        "AI agents and MCP servers",
        "Process automation with n8n",
        "GDPR-compliant operation, on-premise with Ollama where required",
      ],
    },
  },
  {
    id: "web-apps",
    route: "solutions-web-apps",
    name: { de: "Web-Applikationen", en: "Web applications" },
    summary: {
      de: "High-Performance Web-Applikationen mit Next.js und React — vom interaktiven Kundenportal bis zur komplexen SaaS-Plattform.",
      en: "High-performance web applications with Next.js and React — from an interactive customer portal to a complex SaaS platform.",
    },
    covers: {
      de: [
        "Kundenportale und interne Werkzeuge",
        "SaaS-Plattformen",
        "Bestehende Anwendungen modernisieren",
      ],
      en: [
        "Customer portals and internal tools",
        "SaaS platforms",
        "Modernising existing applications",
      ],
    },
  },
  {
    id: "mobile-apps",
    route: "solutions-mobile-apps",
    name: { de: "Mobile Apps", en: "Mobile apps" },
    summary: {
      de: "Cross-platform Apps für iOS und Android mit Flutter — ein Codebase, zwei Stores, native Performance.",
      en: "Cross-platform apps for iOS and Android with Flutter — one codebase, two stores, native performance.",
    },
    covers: {
      de: [
        "Flutter-Apps für iOS und Android",
        "Anbindung an bestehende Backends",
        "Veröffentlichung in beiden Stores",
      ],
      en: [
        "Flutter apps for iOS and Android",
        "Integration with existing backends",
        "Publishing to both stores",
      ],
    },
  },
  {
    id: "backend",
    route: "solutions-backend",
    name: { de: "Backend-Systeme", en: "Backend systems" },
    summary: {
      de: "Robuste, skalierbare Backend-Systeme mit Node.js oder Python — von REST-APIs bis zu Event-Driven Architekturen.",
      en: "Robust, scalable backend systems with Node.js or Python — from REST APIs to event-driven architectures.",
    },
    covers: {
      de: [
        "REST- und GraphQL-APIs",
        "Event-getriebene Architekturen",
        "Integration bestehender Systeme",
      ],
      en: [
        "REST and GraphQL APIs",
        "Event-driven architectures",
        "Integrating existing systems",
      ],
    },
  },
  {
    id: "devops",
    route: "solutions-devops",
    name: { de: "DevOps & Cloud", en: "DevOps & Cloud" },
    summary: {
      de: "Cloud-Infrastruktur, CI/CD-Pipelines und GitOps — damit Ihr Code zuverlässig und sicher in Production läuft.",
      en: "Cloud infrastructure, CI/CD pipelines and GitOps — so your code runs reliably and safely in production.",
    },
    covers: {
      de: [
        "CI/CD-Pipelines und GitOps",
        "Container und Kubernetes",
        "Infrastruktur als Code",
      ],
      en: [
        "CI/CD pipelines and GitOps",
        "Containers and Kubernetes",
        "Infrastructure as code",
      ],
    },
  },
  {
    id: "consulting",
    route: "solutions-consulting",
    name: { de: "Consulting", en: "Consulting" },
    summary: {
      de: "Unabhängige Beratung zu Technologie, Cybersecurity und Regulierung — EU AI Act, CRA und NIS-2 als Engineering-Aufgabe.",
      en: "Independent advice on technology, cybersecurity and regulation — the EU AI Act, CRA and NIS 2 as an engineering task.",
    },
    covers: {
      de: [
        "Technologieentscheidungen und Architekturreviews",
        "Vendor-Evaluierung",
        "Begleitung laufender Projekte",
        "Threat Modeling, Security-Reviews und Härtung vor dem Go-live",
        "Was EU AI Act, Cyber Resilience Act und NIS-2-Richtlinie technisch bedeuten: SBOM, Update-Pfad, Audit-Logs, nachvollziehbare KI-Funktionen",
      ],
      en: [
        "Technology decisions and architecture reviews",
        "Vendor evaluation",
        "Oversight of running projects",
        "Threat modeling, security reviews and hardening before go-live",
        "What the EU AI Act, the Cyber Resilience Act and the NIS 2 Directive mean technically: SBOM, update path, audit logs, traceable AI features",
      ],
    },
  },
];

/**
 * The summary a solution page publishes as its description, server-side in
 * its metadata and client-side through `MetadataUpdater`.
 */
export function serviceSummary(id: string): ServiceRecord["summary"] {
  const service = SERVICES.find((s) => s.id === id);
  if (!service) throw new Error(`No service record with id "${id}".`);
  return service.summary;
}
